import { Badge, ExpandableSection, Input, SpaceBetween, Table } from '@amzn/awsui-components-react';
import React from 'react';
import { Event, EventScoringSettings, EventStatus } from '../../../types/Event';
import { toLower } from 'lodash';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '../../../utils/i18n.utils';
import { EditEventActions, useEditEvent } from '../../../store/edit-event.context';
import { useUser } from '../../../store/user.context';
import { Campaign } from '../../../types/Campaign';
import { EditCampaignActions, useEditCampaign } from '../../../store/edit-campaign.context';
import { ChallengeDifficulty } from '../../../types/Challenge';
import useInputBuffer from '@/src/hooks/useInputBuffer';

// Fixed set of editable fields, so each can get its own top-level hook.
type EditableScoringField = Extract<
  keyof EventScoringSettings,
  'easyScore' | 'mediumScore' | 'hardScore' | 'clue1PenaltyPercent' | 'clue2PenaltyPercent' | 'clue3PenaltyPercent'
>;

interface ScoringProps {
  target: Event | Campaign;
}

const Scoring: React.FC<ScoringProps> = ({ target }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { editMode, handleUpdateEditEvent, editedEvent } = useEditEvent();
  const { campaignEditMode, editedCampaign, handleUpdateEditCampaign } = useEditCampaign();
  const isEvent = target instanceof Event;
  const canEdit = editMode || campaignEditMode;

  const DifficultyDictionary: { [key: string]: string } = {
    Easy: t(ChallengeDifficulty.FUNDAMENTAL.i18nKeyShort),
    Medium: t(ChallengeDifficulty.INTERMEDIATE.i18nKeyShort),
    Hard: t(ChallengeDifficulty.ADVANCED.i18nKeyShort),
    Expert: t(ChallengeDifficulty.EXPERT.i18nKeyShort),
  };

  const scoringSettings = isEvent
    ? target.scoringSettings
    : target.campaignSettings?.scoringSettings || new EventScoringSettings();
  const editedTarget = isEvent ? editedEvent : editedCampaign?.campaignSettings;

  const commitScoringSetting = (scoringSettingProperty: EditableScoringField, value: string) => {
    const parsedValue = Number(value);
    if (isEvent) {
      handleUpdateEditEvent(EditEventActions.SCORING_SETTINGS, {
        ...editedTarget?.scoringSettings,
        [scoringSettingProperty]: parsedValue,
      });
    } else {
      handleUpdateEditCampaign(EditCampaignActions.SCORING_SETTINGS, {
        ...editedTarget?.scoringSettings,
        [scoringSettingProperty]: parsedValue,
      });
    }
  };

  // One hook per field so the input stays clearable; parsing lives in commitScoringSetting.
  const scoringInputs = {
    easyScore: useInputBuffer(
      editedTarget?.scoringSettings?.easyScore ?? 0,
      (inputValue) => commitScoringSetting('easyScore', inputValue),
      { snapBackOnEmpty: true }
    ),
    mediumScore: useInputBuffer(
      editedTarget?.scoringSettings?.mediumScore ?? 0,
      (inputValue) => commitScoringSetting('mediumScore', inputValue),
      { snapBackOnEmpty: true }
    ),
    hardScore: useInputBuffer(
      editedTarget?.scoringSettings?.hardScore ?? 0,
      (inputValue) => commitScoringSetting('hardScore', inputValue),
      { snapBackOnEmpty: true }
    ),
    clue1PenaltyPercent: useInputBuffer(
      editedTarget?.scoringSettings?.clue1PenaltyPercent ?? 0,
      (inputValue) => commitScoringSetting('clue1PenaltyPercent', inputValue),
      { snapBackOnEmpty: true }
    ),
    clue2PenaltyPercent: useInputBuffer(
      editedTarget?.scoringSettings?.clue2PenaltyPercent ?? 0,
      (inputValue) => commitScoringSetting('clue2PenaltyPercent', inputValue),
      { snapBackOnEmpty: true }
    ),
    clue3PenaltyPercent: useInputBuffer(
      editedTarget?.scoringSettings?.clue3PenaltyPercent ?? 0,
      (inputValue) => commitScoringSetting('clue3PenaltyPercent', inputValue),
      { snapBackOnEmpty: true }
    ),
  } satisfies Record<EditableScoringField, ReturnType<typeof useInputBuffer>>;

  const getDeduction = (clueNumber: number, difficulty: string): number => {
    difficulty = toLower(difficulty);
    if (editedTarget?.scoringSettings || scoringSettings) {
      const penaltyProperty = `clue${clueNumber}PenaltyPercent` as keyof EventScoringSettings;
      const scoreProperty = `${difficulty}Score` as keyof EventScoringSettings;
      const multiplier = editedTarget?.scoringSettings
        ? (editedTarget.scoringSettings[penaltyProperty] || 0) / 100
        : (scoringSettings[penaltyProperty] || 0) / 100;

      return Math.round(
        multiplier *
          (editedTarget?.scoringSettings
            ? editedTarget.scoringSettings[scoreProperty] || 0
            : scoringSettings[scoreProperty] || 0)
      );
    } else {
      return 0;
    }
  };

  const getTotalDeduction = (cluesUsed: number, difficulty: string): number => {
    let clueCount = 1;
    let totalPenalty = 0;
    while (clueCount <= cluesUsed) {
      totalPenalty += getDeduction(clueCount, difficulty);
      clueCount++;
    }
    return totalPenalty;
  };

  const getAdjustedScore = (cluesUsed: number, difficulty: string): number => {
    if (editedTarget?.scoringSettings || scoringSettings) {
      difficulty = toLower(difficulty);
      const scoreProperty = `${difficulty}Score` as keyof EventScoringSettings;
      const totalDeduction = getTotalDeduction(cluesUsed, difficulty);

      return (
        (editedTarget?.scoringSettings
          ? editedTarget.scoringSettings[scoreProperty] || 0
          : scoringSettings[scoreProperty] || 0) - totalDeduction
      );
    } else {
      return 0;
    }
  };

  return (
    <ExpandableSection
      variant="container"
      headerText={t(i18nKeys.events.eventDetails.headers.scoring.header)}
      data-testid="scoring-expandable"
    >
      <div>
        <ul style={{ margin: 0, padding: '0 16px' }}>
          <li>{t(i18nKeys.events.eventDetails.headers.scoring.description1)}</li>
          <li>{t(i18nKeys.events.eventDetails.headers.scoring.description2)}</li>
          <li>{t(i18nKeys.events.eventDetails.headers.scoring.description3)}</li>
        </ul>
      </div>
      <div style={{ marginTop: '16px' }}>
        <Table
          columnDefinitions={[
            {
              id: 'difficulty',
              header: t(i18nKeys.events.eventDetails.headers.scoring.difficulty),
              cell: (e) => DifficultyDictionary[e.difficulty],
            },
            {
              id: 'score',
              header: t(i18nKeys.events.eventDetails.headers.scoring.score),
              cell: (e) =>
                canEdit && user ? (
                  <span>
                    <Input
                      className="numeric-input"
                      inputMode="numeric"
                      data-testid={toLower(e.difficulty) + 'Score'}
                      onChange={({ detail }) =>
                        scoringInputs[(toLower(e.difficulty) + 'Score') as EditableScoringField].handleChange(
                          detail.value
                        )
                      }
                      disabled={
                        !target?.canEditAttribute('scoringSettings', user) || target.status === EventStatus.ONGOING
                      }
                      value={scoringInputs[(toLower(e.difficulty) + 'Score') as EditableScoringField].value}
                      onBlur={scoringInputs[(toLower(e.difficulty) + 'Score') as EditableScoringField].handleBlur}
                    />{' '}
                    {t(i18nKeys.eventReport.points).toLocaleLowerCase()}
                  </span>
                ) : (
                  <span>{t(i18nKeys.challenges.challengeSelection.maxScore.value, { points: e.score })}</span>
                ),
            },
            {
              id: 'clue-1-penalty',
              header:
                canEdit && user ? (
                  <span>
                    {t(i18nKeys.events.eventDetails.headers.scoring.clue1PenaltyEdit)}
                    <br />
                    <Input
                      className="numeric-input"
                      inputMode="numeric"
                      data-testid="clue1PenaltyPercent"
                      disabled={
                        !target?.canEditAttribute('scoringSettings', user) || target.status === EventStatus.ONGOING
                      }
                      value={scoringInputs.clue1PenaltyPercent.value}
                      onChange={({ detail }) => scoringInputs.clue1PenaltyPercent.handleChange(detail.value)}
                      onBlur={scoringInputs.clue1PenaltyPercent.handleBlur}
                    />
                    {'  %'}
                  </span>
                ) : (
                  t(i18nKeys.events.eventDetails.headers.scoring.clue1Penalty, {
                    penaltyPercent: scoringSettings?.clue1PenaltyPercent,
                  })
                ),
              cell: (e) => (
                <SpaceBetween direction="horizontal" size="m">
                  <Badge color="green">
                    {t(i18nKeys.challenges.challengeSelection.maxScore.value, {
                      points: getAdjustedScore(1, e.difficulty),
                    })}
                  </Badge>
                  <span>
                    - {getDeduction(1, e.difficulty)} {t(i18nKeys.events.eventDetails.headers.scoring.deduction)}
                  </span>
                </SpaceBetween>
              ),
            },
            {
              id: 'clue-2-penalty',
              header:
                canEdit && user ? (
                  <span>
                    {t(i18nKeys.events.eventDetails.headers.scoring.clue2PenaltyEdit)}
                    <br />
                    <Input
                      className="numeric-input"
                      inputMode="numeric"
                      data-testid="clue2PenaltyPercent"
                      disabled={
                        !target?.canEditAttribute('scoringSettings', user) || target.status === EventStatus.ONGOING
                      }
                      value={scoringInputs.clue2PenaltyPercent.value}
                      onChange={({ detail }) => scoringInputs.clue2PenaltyPercent.handleChange(detail.value)}
                      onBlur={scoringInputs.clue2PenaltyPercent.handleBlur}
                    />
                    {'  %'}
                  </span>
                ) : (
                  t(i18nKeys.events.eventDetails.headers.scoring.clue2Penalty, {
                    penaltyPercent: scoringSettings?.clue2PenaltyPercent,
                  })
                ),
              cell: (e) => (
                <SpaceBetween direction="horizontal" size="m">
                  <Badge color="blue">
                    {t(i18nKeys.challenges.challengeSelection.maxScore.value, {
                      points: getAdjustedScore(2, e.difficulty),
                    })}
                  </Badge>
                  <span>
                    - {getDeduction(2, e.difficulty)} {t(i18nKeys.events.eventDetails.headers.scoring.deduction)}
                  </span>
                </SpaceBetween>
              ),
            },
            {
              id: 'clue-3-penalty',
              header:
                canEdit && user ? (
                  <span>
                    {t(i18nKeys.events.eventDetails.headers.scoring.clue3PenaltyEdit)}
                    <br />
                    <Input
                      className="numeric-input"
                      inputMode="numeric"
                      data-testid="clue3PenaltyPercent"
                      disabled={
                        !target?.canEditAttribute('scoringSettings', user) || target.status === EventStatus.ONGOING
                      }
                      value={scoringInputs.clue3PenaltyPercent.value}
                      onChange={({ detail }) => scoringInputs.clue3PenaltyPercent.handleChange(detail.value)}
                      onBlur={scoringInputs.clue3PenaltyPercent.handleBlur}
                    />
                    {'  %'}
                  </span>
                ) : (
                  t(i18nKeys.events.eventDetails.headers.scoring.clue3Penalty, {
                    penaltyPercent: scoringSettings?.clue3PenaltyPercent,
                  })
                ),
              cell: (e) => (
                <SpaceBetween direction="horizontal" size="m">
                  <Badge color="red">
                    {t(i18nKeys.challenges.challengeSelection.maxScore.value, {
                      points: getAdjustedScore(3, e.difficulty),
                    })}
                  </Badge>
                  <span>
                    - {getDeduction(3, e.difficulty)} {t(i18nKeys.events.eventDetails.headers.scoring.deduction)}
                  </span>
                </SpaceBetween>
              ),
            },
          ]}
          items={[
            {
              difficulty: 'Easy',
              score: scoringSettings?.easyScore,
            },
            {
              difficulty: 'Medium',
              score: scoringSettings?.mediumScore,
            },
            {
              difficulty: 'Hard',
              score: scoringSettings?.hardScore,
            },
          ]}
        />
      </div>
    </ExpandableSection>
  );
};

export default Scoring;
